import React, { useCallback, useEffect, useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { Church, Handshake, Lightbulb, Search } from "lucide-react"
import { getApiBaseUrl } from "../../config/apiBase"
import "../../styles/components/FellowshipPanel.css"

const churchType = (group) => ["church", "congregation", "parish"].includes(String(group?.groupType || "").toLowerCase())
const fellowshipType = (group) => String(group?.groupType || "").toLowerCase() === "fellowship"

export default function FellowshipPanel({ group }) {
  const history = useHistory()
  const { getAccessTokenSilently } = useAuth0()
  const [overview, setOverview] = useState(null)
  const [churches, setChurches] = useState([])
  const [query, setQuery] = useState("")
  const [targetId, setTargetId] = useState("")
  const [name, setName] = useState("")
  const [purpose, setPurpose] = useState("")
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState("")
  const [error, setError] = useState("")
  const enabled = churchType(group) || fellowshipType(group)

  const request = useCallback(async (path, options = {}) => {
    const token = await getAccessTokenSilently()
    const response = await fetch(`${getApiBaseUrl()}/api/fellowships/${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    })
    const body = await response.json().catch(() => null)
    if (!response.ok) throw new Error(body?.detail || "The fellowship request could not be completed.")
    return body
  }, [getAccessTokenSilently])

  const load = useCallback(async () => {
    if (!enabled || !group?.id) return
    setOverview(await request(`groups/${group.id}/overview`))
  }, [enabled, group?.id, request])

  useEffect(() => { load().catch((reason) => setError(reason.message)) }, [load])
  useEffect(() => {
    if (!enabled) return undefined
    const handle = window.setTimeout(() => {
      request(`churches?query=${encodeURIComponent(query)}&excludeGroupId=${group.id}`)
        .then(setChurches).catch((reason) => setError(reason.message))
    }, 250)
    return () => window.clearTimeout(handle)
  }, [enabled, group?.id, query, request])

  if (!enabled) return null

  const propose = async (event) => {
    event.preventDefault()
    if (!targetId || (churchType(group) && !name.trim())) return
    setBusy(true); setError(""); setNotice("")
    try {
      const body = churchType(group)
        ? { originCongregationId: group.id, targetCongregationId: Number(targetId), fellowshipName: name.trim(), purpose }
        : { targetCongregationId: Number(targetId), purpose }
      const path = churchType(group) ? "proposals" : `${group.id}/invitations`
      const result = await request(path, { method: "POST", body: JSON.stringify(body) })
      setTargetId(""); setName(""); setPurpose("")
      setNotice("The Fellowship idea is now open for your community to consider.")
      await load()
      history.replace(`/group-members/${group.id}?tab=ideas&campaignId=${result.originCampaignId}`)
    } catch (reason) { setError(reason.message) }
    finally { setBusy(false) }
  }

  const campaignLink = (item) => {
    const received = Number(item.targetCongregationId) === Number(group.id) && item.targetCampaignId
    const campaignId = received ? item.targetCampaignId : item.originCampaignId
    return campaignId ? `/group-members/${group.id}?tab=ideas&campaignId=${campaignId}` : null
  }

  return <section className="fellowship-panel">
    <div className="fellowship-panel-heading">
      <span><Handshake size={18} /> Congregational fellowship</span>
      <h2>{fellowshipType(group) ? "Churches walking together" : "Grow closer to neighboring churches"}</h2>
      <p>{fellowshipType(group)
        ? "This is a shared space for equal, independent congregations. Members can bring forward ideas, plan gatherings, pray, and coordinate service together."
        : "A fellowship is formed by mutual discernment—not by one church enrolling another. Each congregation approves the relationship for itself and keeps its own leadership, identity, property, and finances."}</p>
    </div>

    {overview?.fellowships?.length > 0 && <div className="fellowship-list">
      <h3>Your church’s fellowships</h3>
      {overview.fellowships.map((item) => <Link key={item.id} to={`/group-members/${item.id}`}><Handshake size={17} /> {item.groupName}</Link>)}
    </div>}
    {overview?.congregations?.length > 0 && <div className="fellowship-church-grid">
      {overview.congregations.map((item) => <Link key={item.id} to={`/group/${item.id}`}>
        <Church size={20} /><strong>{item.groupName}</strong><small>{[item.city, item.state].filter(Boolean).join(", ")}</small>
        {item.isFoundingCongregation && <em>Founding congregation</em>}
      </Link>)}
    </div>}

    {overview?.pending?.length > 0 && <div className="fellowship-pending">
      <h3>Fellowship decisions in progress</h3>
      {overview.pending.map((item) => {
        const link = campaignLink(item)
        const awaitingYourChurch = Number(item.targetCongregationId) === Number(group.id)
        return <div key={item.id}><Lightbulb size={16} /><span><strong>{item.fellowshipName}</strong><small>{item.status === "AwaitingTargetApproval" ? (awaitingYourChurch ? "The invitation is ready for your congregation’s decision." : "Your side approved; waiting for the invited congregation.") : "Open for your community’s decision."}</small></span>
          {link && <Link to={link}>Open idea</Link>}</div>
      })}
    </div>}

    <form onSubmit={propose} className="fellowship-proposal-form">
      <h3>{fellowshipType(group) ? "Suggest inviting another congregation" : "Suggest forming a fellowship"}</h3>
      <p><Lightbulb size={16} /> This creates a focused idea with no refinement stage and no arbitrary deadline. If your community approves, the invited church gets its own decision. Nothing forms until both sides say yes.</p>
      <label><span><Search size={15} /> Find a congregation</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by church, city, or county" /></label>
      <label>Congregation<select required value={targetId} onChange={(event) => setTargetId(event.target.value)}><option value="">Choose a congregation</option>
        {churches.map((item) => <option key={item.id} value={item.id}>{item.groupName}{item.city ? ` · ${item.city}` : ""}</option>)}</select></label>
      {churchType(group) && <label>Fellowship name<input required maxLength="150" value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Niagara Neighbors in Christ" /></label>}
      <label>Shared hope or purpose <span className="optional">optional</span><textarea maxLength="1000" value={purpose} onChange={(event) => setPurpose(event.target.value)} placeholder="Prayer, shared meals, worship, friendship, and serving our neighbors…" /></label>
      {error && <p role="alert" className="fellowship-error">{error}</p>}
      {notice && <p role="status" className="fellowship-notice">{notice}</p>}
      <button disabled={busy || !targetId}>{busy ? "Opening idea…" : "Open Fellowship idea"}</button>
    </form>
  </section>
}
